/**
 * Theme Adapter
 *
 * Transforms API theme data into standardized design tokens.
 * This bridge layer handles field mapping, derives missing values,
 * and ensures consistency across the application.
 */

import { DEFAULT_COLORS, TYPOGRAPHY_SCALE, FONT_WEIGHTS } from './tokens';
import {
  adjustOpacity,
  lightenColor,
  darkenColor,
  parseFontSize,
} from './utils';

/**
 * Transform API theme data into design tokens
 * @param {object} apiTheme - Theme data from API (can be null/undefined)
 * @returns {object} Standardized theme tokens
 */
export const createThemeTokens = apiTheme => {
  // Handle null/undefined theme - use defaults
  const theme = apiTheme || {};

  // Base colors from API with fallbacks to DEFAULT_COLORS
  const primaryColor = theme.primary_color || DEFAULT_COLORS.primary;
  const secondaryColor = theme.secondary_color || DEFAULT_COLORS.secondary;
  const accentColor = theme.accent_color || DEFAULT_COLORS.accent;
  const backgroundColor = theme.background_color || DEFAULT_COLORS.background;
  const surfaceColor = theme.surface_color || DEFAULT_COLORS.surface;
  const textColor = theme.text_color || DEFAULT_COLORS.text;
  // Additional theme properties
  const sidebarBackground = theme.sidebar_background || surfaceColor;
  const sidebarText = theme.sidebar_text || textColor;
  const textSecondary = theme.text_secondary || adjustOpacity(textColor, 0.7);
  const textMuted = theme.text_muted || adjustOpacity(textColor, 0.45);
  const textDisabled = theme.text_disabled || adjustOpacity(textColor, 0.28);
  const dividerColor = theme.divider_color || adjustOpacity(textColor, 0.12);
  const themeMode = theme.mode || 'light';

  // Derive color variations
  const tokens = {
    // ========================================================================
    // COLOR TOKENS
    // ========================================================================
    colors: {
      // Primary palette
      primary: {
        main: primaryColor,
        light: lightenColor(primaryColor, 20),
        dark: darkenColor(primaryColor, 20),
        contrastText: '#ffffff',
      },

      // Secondary palette
      secondary: {
        main: secondaryColor,
        light: lightenColor(secondaryColor, 20),
        dark: darkenColor(secondaryColor, 20),
        contrastText: '#ffffff',
      },

      // Accent color
      accent: {
        main: accentColor,
        light: lightenColor(accentColor, 20),
        dark: darkenColor(accentColor, 20),
        contrastText: '#ffffff',
      },

      // Status colors (use theme success_color if available, otherwise use default green)
      success: {
        main: theme.success_color || '#27AE60', // Default to darker green
        light: theme.success_color
          ? lightenColor(theme.success_color, 20)
          : '#2ECC71', // Default lighter green
        dark: theme.success_color
          ? darkenColor(theme.success_color, 20)
          : '#1E8449', // Default darker green
        contrastText: '#ffffff',
      },

      warning: {
        main: theme.warning_color || '#FFA02A', // Bright orange - Total Cost card
        light: theme.warning_color
          ? lightenColor(theme.warning_color, 20)
          : '#FFB855', // Lighter orange
        dark: theme.warning_color
          ? darkenColor(theme.warning_color, 20)
          : '#CC8000', // Darker orange
        contrastText: '#ffffff',
      },

      error: {
        // Use theme error_color if provided, otherwise use a fixed red color
        // Error colors should always be red, not derived from primary color
        main: theme.error_color || '#EF4444', // Modern red - consistent error color
        light: theme.error_color
          ? lightenColor(theme.error_color, 20)
          : '#F87171', // Lighter red
        dark: theme.error_color
          ? darkenColor(theme.error_color, 20)
          : '#DC2626', // Darker red
        contrastText: '#ffffff',
      },

      info: {
        main: theme.info_color || '#2AC0C4', // Teal/Cyan - Success Rate card
        light: theme.info_color
          ? lightenColor(theme.info_color, 20)
          : '#4AE0E4', // Lighter teal
        dark: theme.info_color ? darkenColor(theme.info_color, 20) : '#1A9094', // Darker teal
        contrastText: '#ffffff',
      },

      // Background colors
      background: {
        default: backgroundColor,
        paper: surfaceColor,
      },
      // Sidebar colors
      sidebar: {
        background: sidebarBackground,
        text: sidebarText,
      },

      // Text colors with proper tiers
      text: {
        primary: textColor,
        secondary: textSecondary,
        muted: textMuted,
        disabled: textDisabled,
      },

      // Divider and borders
      divider: dividerColor,
      border: adjustOpacity(textColor, 0.23),
    },

    // ========================================================================
    // TYPOGRAPHY TOKENS
    // ========================================================================
    typography: {
      fontFamily:
        theme.font_family ||
        "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif",

      fontSize: parseFontSize(theme.font_size) || TYPOGRAPHY_SCALE.BASE_SIZE,

      fontWeightLight: FONT_WEIGHTS.LIGHT,
      fontWeightRegular:
        parseInt(theme.font_weight_normal) || FONT_WEIGHTS.REGULAR,
      fontWeightMedium: FONT_WEIGHTS.MEDIUM,
      fontWeightBold: parseInt(theme.font_weight_bold) || FONT_WEIGHTS.BOLD,

      // Heading configurations
      h1: {
        fontSize:
          theme.heading_font_size || `${TYPOGRAPHY_SCALE.BASE_SIZE * 2.5}px`,
        fontWeight: parseInt(theme.font_weight_bold) || FONT_WEIGHTS.BOLD,
        lineHeight: 1.2,
      },
      h2: {
        fontSize:
          theme.heading_font_size || `${TYPOGRAPHY_SCALE.BASE_SIZE * 2}px`,
        fontWeight: parseInt(theme.font_weight_bold) || FONT_WEIGHTS.BOLD,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: `${TYPOGRAPHY_SCALE.BASE_SIZE * 1.75}px`,
        fontWeight: parseInt(theme.font_weight_bold) || FONT_WEIGHTS.BOLD,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: `${TYPOGRAPHY_SCALE.BASE_SIZE * 1.5}px`,
        fontWeight: parseInt(theme.font_weight_bold) || FONT_WEIGHTS.BOLD,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: `${TYPOGRAPHY_SCALE.BASE_SIZE * 1.25}px`,
        fontWeight: FONT_WEIGHTS.SEMI_BOLD,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: `${TYPOGRAPHY_SCALE.BASE_SIZE * 1.125}px`,
        fontWeight: FONT_WEIGHTS.SEMI_BOLD,
        lineHeight: 1.4,
      },

      // Body text
      body1: {
        fontSize: parseFontSize(theme.font_size) || '14px',
        fontWeight: parseInt(theme.font_weight_normal) || FONT_WEIGHTS.REGULAR,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: `${(parseFontSize(theme.font_size) || 14) * 0.875}px`,
        fontWeight: parseInt(theme.font_weight_normal) || FONT_WEIGHTS.REGULAR,
        lineHeight: 1.5,
      },
    },

    // ========================================================================
    // SPACING TOKENS
    // ========================================================================
    spacing: {
      unit: parseInt(theme.spacing_unit) || 8,
      borderRadius: parseInt(theme.border_radius) || 8,
    },

    // ========================================================================
    // ASSETS
    // ========================================================================
    assets: {
      logo: theme.logo_url || '',
      favicon: theme.favicon_url || '',
    },

    // ========================================================================
    // CUSTOM CSS
    // ========================================================================
    customCSS: theme.custom_css || '',

    // ========================================================================
    // THEME MODE
    // ========================================================================
    mode: themeMode,

    // ========================================================================
    // METADATA
    // ========================================================================
    metadata: {
      themeId: theme.theme_id || null,
      themeName: theme.theme_name || 'Default Theme',
      themeDescription: theme.theme_description || '',
      isActive: theme.is_active || theme.status === 'active',
      status: theme.status || 'inactive',
    },
  };

  return tokens;
};

/**
 * Transform design tokens back to API theme format
 * @param {object} themeTokens - Design tokens
 * @returns {object} API-compatible theme data
 */
export const tokensToApiTheme = themeTokens => {
  return {
    theme_id: themeTokens.metadata?.themeId || null,
    theme_name: themeTokens.metadata?.themeName || '',
    theme_description: themeTokens.metadata?.themeDescription || '',

    // Colors
    primary_color: themeTokens.colors?.primary?.main || DEFAULT_COLORS.primary,
    secondary_color:
      themeTokens.colors?.secondary?.main || DEFAULT_COLORS.secondary,
    accent_color: themeTokens.colors?.accent?.main || DEFAULT_COLORS.accent,
    background_color:
      themeTokens.colors?.background?.default || DEFAULT_COLORS.background,
    surface_color:
      themeTokens.colors?.background?.paper || DEFAULT_COLORS.surface,
    text_color: themeTokens.colors?.text?.primary || DEFAULT_COLORS.text,

    // Typography
    font_family:
      themeTokens.typography?.fontFamily ||
      "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif",
    font_size: `${themeTokens.typography?.fontSize || 14}px`,
    heading_font_size: themeTokens.typography?.h1?.fontSize || `${14 * 2.5}px`,
    font_weight_normal: `${themeTokens.typography?.fontWeightRegular || 400}`,
    font_weight_bold: `${themeTokens.typography?.fontWeightBold || 700}`,

    // Spacing
    border_radius: themeTokens.spacing?.borderRadius || 8,
    spacing_unit: themeTokens.spacing?.unit || 8,

    // Assets
    logo_url: themeTokens.assets?.logo || '',
    favicon_url: themeTokens.assets?.favicon || '',

    // Custom
    custom_css: themeTokens.customCSS || '',

    // Status
    is_active: themeTokens.metadata?.isActive || false,
    status: themeTokens.metadata?.status || 'inactive',
  };
};

/**
 * Merge partial theme updates with existing theme
 * @param {object} currentTheme - Current theme data
 * @param {object} updates - Partial theme updates
 * @returns {object} Merged theme data
 */
export const mergeThemeUpdates = (currentTheme, updates) => {
  return {
    ...currentTheme,
    ...updates,
    // Preserve metadata unless explicitly updated
    metadata: {
      ...currentTheme.metadata,
      ...updates.metadata,
    },
  };
};

const themeAdapter = {
  createThemeTokens,
  tokensToApiTheme,
  mergeThemeUpdates,
};

export default themeAdapter;
